/**
 * Laden, Zwischenspeichern und Ungültigmachen an EINER Stelle (IS-32, ADR 0001).
 *
 * TanStack Query, damit nicht jede Komponente ihren eigenen `useEffect` mit
 * eigenem Ladezustand, eigenem Fehlerzustand und eigener Wiederholungslogik
 * bekommt — das ist der Weg, auf dem eine Oberfläche vier verschiedene
 * Ladebalken bekommt und drei davon hängenbleiben.
 */
import { QueryClient, type QueryClientConfig, useQuery } from '@tanstack/react-query'

import { ApiFehler } from './client'
import { me } from './endpunkte'

/** Schlüssel an einer Stelle: sonst macht ein Tippfehler den Cache still doppelt. */
export const SCHLUESSEL = {
  me: ['auth', 'me'] as const,
} as const

/**
 * `abweichend` gibt es für Tests: dort soll `retry: false` gelten, damit ein
 * erwarteter Fehler sofort ankommt statt nach zwei Wartezeiten. Hereingegeben,
 * nicht gepatcht (CLAUDE.md Abschnitt 2) — und die Vorgaben bleiben die echten.
 */
export function erzeugeQueryClient(
  abweichend: QueryClientConfig['defaultOptions'] = {},
): QueryClient {
  return new QueryClient({
    defaultOptions: {
      ...abweichend,
      queries: {
        /**
         * **Bei 401 und 403 nicht wiederholen.** Beides ist eine Antwort, kein
         * Aussetzer: die Sitzung fehlt, oder das Recht fehlt. Ein erneuter
         * Versuch ändert daran nichts — er belastet nur die Anmelde-Drossel
         * (`app/core/login.py`) mit Treffern, die niemandem helfen.
         */
        retry: (versuch, fehler) => {
          if (fehler instanceof ApiFehler && (fehler.istAbgemeldet || fehler.istVerboten)) {
            return false
          }
          return versuch < 2
        },
        staleTime: 30_000,
        refetchOnWindowFocus: false,
        ...abweichend.queries,
      },
    },
  })
}

/**
 * Der angemeldete Nutzer — oder `null`, wenn niemand angemeldet ist.
 *
 * Ein 401 ist hier **kein Fehler, sondern eine Auskunft**: „niemand angemeldet".
 * Würde er als Fehler durchgereicht, müsste jede aufrufende Stelle denselben
 * Sonderfall behandeln, und eine davon vergisst es und zeigt einen roten Kasten,
 * wo „bitte anmelden" hingehört.
 *
 * Nicht erreichbar bleibt dagegen ein Fehler — „nicht nachgesehen" ist etwas
 * anderes als „nicht angemeldet".
 */
export function useMeineSitzung() {
  return useQuery({
    queryKey: SCHLUESSEL.me,
    queryFn: async () => {
      try {
        return await me()
      } catch (fehler) {
        if (fehler instanceof ApiFehler && fehler.istAbgemeldet) return null
        throw fehler
      }
    },
  })
}
